import { useCallback } from 'react';
import { toDateKey, isAllDay, toLocalISO, snapTo15Min } from '../../../../../utils/dateUtils';

/**
 * Hook containing universal drop handlers for assignments and resources.
 * Supports both MonthGrid and TimelineGrid.
 */
export function useDropHandlers({
    interaction,
    setInteraction,
    assignments,
    onAssignmentUpdate,
    onDropWorker,
    hourStart,
    dynamicHourWidth,
    GRID_BUFFER
}) {

    const isTimedOnDay = (event, dayKey) => {
        if (!event?.start_datetime || isAllDay(event)) return false;
        const startDayKey = toDateKey(new Date(event.start_datetime));
        const endDayKey = toDateKey(new Date(event.end_datetime || event.start_datetime));
        return dayKey >= startDayKey && dayKey <= endDayKey;
    };

    const buildTimedReorderUpdates = useCallback((draggedAssignment, finalS, finalE, targetDayKey, activeInteraction) => {
        const targetS = new Date(finalS);
        const targetE = new Date(finalE);

        const candidateEvents = (assignments || [])
            .filter(event => String(event.id) !== String(draggedAssignment.id))
            .filter(event => isTimedOnDay(event, targetDayKey));

        const overlapsDragged = (event) => {
            const s = new Date(event.start_datetime);
            const e = new Date(event.end_datetime || event.start_datetime);
            return targetS < e && targetE > s;
        };

        const cluster = [];
        const queue = [];
        candidateEvents.forEach(event => {
            if (overlapsDragged(event)) queue.push(event);
        });

        while (queue.length > 0) {
            const current = queue.shift();
            if (cluster.some(item => String(item.id) === String(current.id))) continue;
            cluster.push(current);

            candidateEvents.forEach(other => {
                const alreadyQueued = cluster.some(item => String(item.id) === String(other.id))
                    || queue.some(item => String(item.id) === String(other.id));
                if (alreadyQueued) return;

                const currentS = new Date(current.start_datetime);
                const currentE = new Date(current.end_datetime || current.start_datetime);
                const otherS = new Date(other.start_datetime);
                const otherE = new Date(other.end_datetime || other.start_datetime);
                const overlapsCurrent = currentS < otherE && currentE > otherS;
                if (overlapsCurrent) queue.push(other);
            });
        }

        const sortedOthers = [...cluster].sort((a, b) => {
            const orderA = Number.isFinite(Number(a.stack_order)) ? Number(a.stack_order) : 0;
            const orderB = Number.isFinite(Number(b.stack_order)) ? Number(b.stack_order) : 0;
            if (orderA !== orderB) return orderA - orderB;

            const startA = new Date(a.start_datetime);
            const startB = new Date(b.start_datetime);
            if (startA.getTime() !== startB.getTime()) return startA - startB;

            return String(a.id).localeCompare(String(b.id));
        });

        const insertAt = Number.isFinite(Number(activeInteraction?.targetLane))
            ? Math.max(0, Math.min(sortedOthers.length, Number(activeInteraction.targetLane)))
            : sortedOthers.length;

        const reordered = [...sortedOthers];
        const movedItem = {
            ...draggedAssignment,
            start_datetime: toLocalISO(finalS),
            end_datetime: toLocalISO(finalE)
        };
        reordered.splice(insertAt, 0, movedItem);

        const updatesById = new Map();
        reordered.forEach((event, index) => {
            const original = String(event.id) === String(draggedAssignment.id)
                ? draggedAssignment
                : assignments.find(item => String(item.id) === String(event.id));

            if (!original) return;

            updatesById.set(String(original.id), {
                ...original,
                start_datetime: String(original.id) === String(draggedAssignment.id) ? toLocalISO(finalS) : (original.start_datetime ? toLocalISO(new Date(original.start_datetime)) : null),
                end_datetime: String(original.id) === String(draggedAssignment.id) ? toLocalISO(finalE) : (original.end_datetime ? toLocalISO(new Date(original.end_datetime)) : null),
                all_day: String(original.id) === String(draggedAssignment.id) ? false : (original.all_day ?? false),
                is_all_day: String(original.id) === String(draggedAssignment.id) ? false : (original.is_all_day ?? false),
                status: String(original.id) === String(draggedAssignment.id)
                    ? ((original.status === 'undatiert' || original.status === 'geplant' || !original.status) ? 'aktiv' : original.status)
                    : original.status,
                stack_order: index
            });
        });

        return Array.from(updatesById.values());
    }, [assignments]);

    const handleDrop = useCallback((e, day, targetAssignment = null) => {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');
        try {
            const dataString = e.dataTransfer.getData('application/json');
            if (!dataString) return;
            const data = JSON.parse(dataString);

            if (data.type === 'worker' || data.type === 'vehicle' || data.type === 'kolonne') {
                let finalS = day;
                const isTimedGrid = e.currentTarget.classList.contains('timeline-time-col');
                
                if (day && isTimedGrid) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const offsetX = (e.clientX - rect.left) - GRID_BUFFER;
                    const hoursFromLeft = offsetX / dynamicHourWidth;
                    const dropTime = new Date(day);
                    dropTime.setHours(hourStart + Math.floor(hoursFromLeft), (hoursFromLeft % 1) * 60);
                    finalS = snapTo15Min(dropTime);
                }
                onDropWorker && onDropWorker(data, finalS, targetAssignment);
            } else if (data.type === 'assignment') {
                const assignment = data.assignment;
                let finalS, finalE;
                const isCurrentDraggedAssignment = interaction && String(interaction.assignment?.id) === String(assignment.id);
                const isTimedGrid = e.currentTarget.classList.contains('timeline-time-col');

                if (data.originDay || !assignment.start_datetime) {
                    finalS = new Date(day);
                    
                    if (isTimedGrid) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const offsetX = (e.clientX - rect.left) - GRID_BUFFER;
                        const hoursFromLeft = offsetX / dynamicHourWidth;
                        finalS.setHours(hourStart + Math.floor(hoursFromLeft), (hoursFromLeft % 1) * 60);
                        finalS = snapTo15Min(finalS);
                        finalE = new Date(finalS.getTime() + 60 * 60 * 1000);
                    } else if (data.originDay) {
                        const originDate = new Date(data.originDay);
                        originDate.setHours(0,0,0,0);
                        const targetDate = new Date(day);
                        targetDate.setHours(0,0,0,0);
                        
                        const dayDiffMs = targetDate.getTime() - originDate.getTime();
                        const oldS = new Date(assignment.start_datetime);
                        const oldE = new Date(assignment.end_datetime);
                        
                        finalS = new Date(oldS.getTime() + dayDiffMs);
                        finalE = new Date(oldE.getTime() + dayDiffMs);
                    } else {
                        // MonthView drop from undated sidebar
                        finalS.setHours(0, 0, 0, 0);
                        finalE = new Date(finalS);
                        finalE.setHours(23, 59, 59, 999);
                    }
                } 
                else if (isCurrentDraggedAssignment) {
                    finalS = interaction.currentS;
                    finalE = interaction.currentE;
                } 
                else {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const offsetX = (e.clientX - rect.left) - GRID_BUFFER;
                    const hoursFromLeft = offsetX / dynamicHourWidth;
                    const dropTime = new Date(day);
                    dropTime.setHours(hourStart + Math.floor(hoursFromLeft), (hoursFromLeft % 1) * 60);
                    finalS = snapTo15Min(dropTime);
                    
                    const isAllDaySource = assignment.start_datetime && isAllDay(new Date(assignment.start_datetime), new Date(assignment.end_datetime));
                    let durationMs = (assignment.start_datetime && assignment.end_datetime && !isAllDaySource)
                        ? new Date(assignment.end_datetime) - new Date(assignment.start_datetime)
                        : 60 * 60 * 1000;
                    
                    if (durationMs <= 0 || isAllDaySource) {
                        durationMs = 60 * 60 * 1000;
                    }
                    finalE = new Date(finalS.getTime() + durationMs);
                }

                const newStatus = (assignment.status === 'undatiert' || assignment.status === 'geplant' || !assignment.status) ? 'aktiv' : assignment.status;

                if (isCurrentDraggedAssignment && (interaction?.type === 'move' || interaction?.type === 'move-allday') && !isAllDay(finalS, finalE)) {
                    const targetDayKey = toDateKey(day);
                    const reorderUpdates = buildTimedReorderUpdates(
                        assignment,
                        finalS,
                        finalE,
                        targetDayKey,
                        {
                            ...interaction,
                            targetLane: interaction?.targetLane
                        }
                    );
                    onAssignmentUpdate(reorderUpdates);
                } else {
                    onAssignmentUpdate({
                        ...assignment,
                        status: newStatus,
                        start_datetime: toLocalISO(finalS),
                        end_datetime: toLocalISO(finalE),
                        all_day: isAllDay(finalS, finalE),
                        is_all_day: isAllDay(finalS, finalE)
                    });
                }
            }
        } catch (err) { 
            console.error("Drop failed:", err);
        } finally {
            setInteraction(null);
        }
    }, [interaction, buildTimedReorderUpdates, onAssignmentUpdate, onDropWorker, hourStart, dynamicHourWidth, GRID_BUFFER]);

    const handleDropAllDay = useCallback((e, day, targetAssignment = null) => {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');
        try {
            const dataString = e.dataTransfer.getData('application/json');
            if (!dataString) return;
            const data = JSON.parse(dataString);
            if (data.type === 'worker' || data.type === 'vehicle' || data.type === 'kolonne') {
                const finalS = new Date(day);
                finalS.setHours(0, 0, 0, 0);
                onDropWorker && onDropWorker(data, finalS, targetAssignment);
            } else if (data.type === 'assignment') {
                const assignment = data.assignment;
                let s, e_dt;
                
                if (interaction && String(interaction.assignment?.id) === String(assignment.id)) {
                    s = new Date(interaction.currentS);
                    e_dt = new Date(interaction.currentE);
                } else {
                    s = new Date(day);
                    s.setHours(0, 0, 0, 0);
                    const durationMs = (assignment.start_datetime && assignment.end_datetime)
                        ? new Date(assignment.end_datetime) - new Date(assignment.start_datetime)
                        : 0;
                    e_dt = new Date(s.getTime() + Math.max(0, durationMs));
                }

                s.setHours(0,0,0,0);
                e_dt.setHours(0,0,0,0);

                const newStatus = (assignment.status === 'undatiert' || assignment.status === 'geplant' || !assignment.status) ? 'aktiv' : assignment.status;

                onAssignmentUpdate({
                    ...assignment,
                    status: newStatus,
                    start_datetime: toLocalISO(s),
                    end_datetime: toLocalISO(e_dt),
                    all_day: true,
                    is_all_day: true
                });
            }
        } catch (err) {
            console.error("Drop all-day failed:", err);
        } finally {
            setInteraction(null);
        }
    }, [interaction, onAssignmentUpdate, onDropWorker]);

    return {
        handleDrop,
        handleDropAllDay,
        buildTimedReorderUpdates
    };
}
